import _ from 'lodash';
import { useCallback, useState } from 'react';
import { calculateVersionedName, getVersionNumberFromLabel } from '@/accessControl/itemAclModal.utilities';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { VersionHistoryOperations } from '@/core/hooks/useVersionState.hook';
import { unsubscribeFromLockingChannel } from '@/main/cleanup.utilities';
import { getViewWorksheetLink, goToWithReload } from '@/main/routing.utilities';
import { emitWorkbook, emitWorkbooks } from '@/services/notifier.service';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { toUserFacingGraphQLError } from '@/utilities/graphql.utilities';
import { subscribeToLockingChannel } from '@/utilities/socket.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { setLockingErrors, setWorkbookDisplayMode } from '@/workbook/workbook.actions';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqWorkbookGqlApi } from '@seeqdev/api/graphql/sqWorkbookGqlApi';
import type { VersionState } from '@/core/hooks/useVersionState.hook';
import type { LoadOptions } from '@/worksheet/worksheet.types';
import type { LockErrorOutputV1 } from '@seeqdev/api/model/LockErrorOutputV1';
import type { LockErrorPathOutputV1 } from '@seeqdev/api/model/LockErrorPathOutputV1';

interface UseLockAndVersionOptions<T> {
  itemId: string;
  /** Called when locking succeeds (before redirect). Use to close the containing modal. */
  closeModal: () => void;
  /** Optional async work to perform after lock+version but before the page redirect. */
  onBeforeRedirect?: () => Promise<T>;
  /** Which UI entry point triggered the lock, for telemetry attribution. Defaults to 'shareModal'. */
  source?: 'shareModal' | 'approvalFlow';
}

export function useLockAndVersion<T>({
  itemId,
  closeModal,
  onBeforeRedirect,
  source = 'shareModal',
}: UseLockAndVersionOptions<T>) {
  const [lockErrors, setLockErrors] = useState<LockErrorOutputV1[] | undefined>(undefined);
  const [hasLockingFailedPreviouslyDueToGlobalItems, setHasLockingFailedPreviouslyDueToGlobalItems] = useState(false);

  const { stateParams } = useFluxPaths(sqWorkbenchStore, ['stateParams']);
  const { name: workbookName, isTopic, isVantage } = useFluxPaths(sqWorkbookStore, ['name', 'isTopic', 'isVantage']);

  const handleLockError = useCallback(
    (response: unknown, workbookId: string, worksheetId: string) => {
      const data = (response as { data?: { errors?: LockErrorOutputV1[] } })?.data;
      if (data?.errors) {
        setHasLockingFailedPreviouslyDueToGlobalItems(true);
        errorToast({ messageKey: 'ACCESS_CONTROL.LOCKING.LOCK_FAILURE' });
        setLockErrors(data.errors);
        doTrack(TRACK.LOCKING_VERSIONING, isVantage ? 'Lock Vantage' : isTopic ? 'Lock Topic' : 'Lock Analysis', {
          lockedSuccessfully: false,
          numErrors: data.errors.length ?? 0,
          workbookId,
          source,
        });

        if (data.errors.length > 0) {
          const globalItemsWithMergedItemPaths: LockErrorOutputV1[] = _.chain(data.errors)
            .groupBy('id')
            .map((group) => ({
              id: group[0].id,
              item: group[0].item,
              itemPath: group
                .flatMap((g: LockErrorOutputV1) => g.itemPath)
                .filter((path): path is LockErrorPathOutputV1 => path !== undefined),
            }))
            .value();

          setLockingErrors(globalItemsWithMergedItemPaths);
        }
      } else {
        errorToast({ httpResponseOrError: toUserFacingGraphQLError(response) });
      }
      const loadOptions: LoadOptions = {
        workbookId,
        worksheetId,
        workbookDisplay: WORKBOOK_DISPLAY.EDIT,
      };
      subscribeToLockingChannel(loadOptions);
    },
    [isVantage, isTopic, source],
  );

  const lockAndVersion = useCallback(
    async (versionState: VersionState) => {
      const { workbookId, worksheetId } = stateParams;
      const originalWorkbookName = workbookName;
      const { versionHistoryOperation, newWorkbookName, selectedVersionHistory, notes } = versionState;

      const finishAndRedirect = async (toastMessageKey = 'ACCESS_CONTROL.LOCKING.LOCKED_TOAST') => {
        successToast({ messageKey: toastMessageKey });
        closeModal();
        setWorkbookDisplayMode(WORKBOOK_DISPLAY.VIEW);
        if (onBeforeRedirect) {
          await onBeforeRedirect();
        }
        goToWithReload(getViewWorksheetLink(workbookId, worksheetId));
      };

      if (isVantage) {
        try {
          unsubscribeFromLockingChannel();
          await sqItemsApi.lockItem({ id: itemId });
          doTrack(TRACK.LOCKING_VERSIONING, 'Lock Vantage', { lockedSuccessfully: true, workbookId, source });
          emitWorkbooks();
          emitWorkbook(workbookId);
          await finishAndRedirect('ACCESS_CONTROL.LOCKING.LOCKED_VANTAGE_TOAST');
        } catch (err) {
          handleLockError(err, workbookId, worksheetId);
        }
      } else {
        try {
          let versionedName: string;
          let versionNumber: number;
          let versionHistoryId: string | undefined;
          let versionHistoryName: string | undefined;

          if (versionHistoryOperation === VersionHistoryOperations.Create) {
            versionNumber = 1;
            const baseName =
              newWorkbookName && newWorkbookName !== originalWorkbookName ? newWorkbookName : originalWorkbookName;
            versionedName = calculateVersionedName(baseName, versionNumber);
            versionHistoryName = baseName;
          } else if (versionHistoryOperation === VersionHistoryOperations.Update && selectedVersionHistory?.value.id) {
            versionNumber = getVersionNumberFromLabel(selectedVersionHistory.label);
            const baseName = selectedVersionHistory.value.name;
            versionedName = calculateVersionedName(baseName, versionNumber);
            versionHistoryId = selectedVersionHistory.value.id;
          } else {
            errorToast({ messageKey: 'ACCESS_CONTROL.LOCKING.VERSIONING.FAILED_NEW_VERSION' });
            return;
          }

          unsubscribeFromLockingChannel();
          const { lockAndVersionWorkbook: result } = await sqWorkbookGqlApi.lockAndVersionWorkbook({
            input: {
              itemId,
              versionedName,
              notes,
              versionHistoryId,
              versionHistoryName,
            },
          });

          const mutationLockErrors = (result.lockErrors ?? []) as LockErrorOutputV1[];
          if (mutationLockErrors.length > 0) {
            handleLockError({ data: { errors: mutationLockErrors } }, workbookId, worksheetId);
            return;
          }

          const finalName = result.name ?? versionedName;
          doTrack(TRACK.LOCKING_VERSIONING, isTopic ? 'Lock and Version Topic' : 'Lock and Version Analysis', {
            lockedSuccessfully: true,
            numErrors: 0,
            workbookId,
            versionNumber: result.versionNumber ?? versionNumber,
            source,
          });
          flux.dispatch('WORKBOOK_SET_PROP', { name: finalName }, PUSH_IGNORE);
          flux.dispatch('WORKBENCH_SET_NAME', { name: finalName, workbookId }, PUSH_IGNORE);
          emitWorkbooks();
          emitWorkbook(workbookId);
          await finishAndRedirect();
        } catch (err) {
          handleLockError(err, workbookId, worksheetId);
        }
      }
    },
    [stateParams, workbookName, isTopic, isVantage, itemId, closeModal, onBeforeRedirect, handleLockError, source],
  );

  const dismissLockErrors = useCallback(() => {
    setHasLockingFailedPreviouslyDueToGlobalItems(false);
  }, []);

  return { lockAndVersion, dismissLockErrors, lockErrors, hasLockingFailedPreviouslyDueToGlobalItems };
}
